import { Table, TableProps } from 'antd';
import { FunctionComponent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

// components
import NoData from 'components/noData/noData';
import LoadingComponent from 'components/loadingComponent/loadingComponent';
import ActionListHeaderComponent from 'modules/main/actions/listHeader';
import ListButton from 'components/listButton/listButton';
import StatusComponent from 'components/status/status';

// services

// styles

// models
import { Announcement } from '../models/announcement';
import { onGetAnnouncementList } from '../services/anouncementServices';
import NotificationService from 'core/services/notification';
import TableTotal from 'components/tableTotal/tableTotal';
import { StatusColors } from 'utils/statusColors';
import { ServiceCost } from '../../commonModels';
import { onGetServiceCost } from '../../commonServices';

interface AnnouncementListPageProps {}

const AnnouncementListPage: FunctionComponent<AnnouncementListPageProps> = () => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const location = useLocation();

	const [isLoading, setIsLoading] = useState<boolean>(true);
	const [requests, setRequests] = useState<Announcement[]>([]);

	const [serviceCostLoading, setServiceCostLoading] = useState<boolean>(true);
	const [serviceCost, setServiceCost] = useState<ServiceCost>();

	const [currentPage, setCurrentPage] = useState<number>(1);
	const [totalRecords, setTotalRecords] = useState<number>(0);

	const columns: TableProps<Announcement>['columns'] = [
		{
			title: t('ACTIONS.REQUEST_CODE'),
			dataIndex: 'request_number',
			key: 'code',
			render: (code) => `#${code}`,
		},
		{
			title: t('ACTIONS.REQUEST_DATE'),
			dataIndex: 'created_at',
			key: 'date',
		},
		{
			title: t('COMMON.STATUS'),
			dataIndex: 'status',
			key: 'status',
			render: (status) => (
				<StatusComponent
					text={
						status === 'Approved'
							? `${t(`COMMON.${status.toUpperCase()}`)} ${t('ACTIONS.WAITING_FOR_PUBLISH')} `
							: t(`COMMON.${status.toUpperCase()}`)
					}
					color={StatusColors[status]}
				/>
			),
		},
		{
			title: t('ACTIONS.NOTIFIED_BY_SMS'),
			dataIndex: 'notified_by_sms',
			key: 'notified_by_sms',
			render: (sms) => t(`COMMON.${sms ? 'YES' : 'NO'}`),
		},
		{
			title: t('ACTIONS.BY'),
			dataIndex: 'by',
			key: 'by',
			render: (_, record) =>  record.company_user ? record.company_user.name : '--',
		},
		{
			title: t('COMMON.ACTIONS'),
			dataIndex: 'actions',
			key: 'actions',
			render: (_, record) => <ListButton type="view" onClick={() => navigate(`${record.id}`)} />,
		},
	];

	useEffect(() => {
		setCurrentPage(getCurrentPage());
		getAnnouncements();
		getServiceCost();
	}, []);

	const getCurrentPage = () => {
		const params = new URLSearchParams(location.search);
		const page = params.get('page');
		return page ? parseInt(page) : 1;
	};

	const getServiceCost = async () => {
		try {
			setServiceCostLoading(true);

			const response = await onGetServiceCost('announcements');
			setServiceCost(response?.success);

			setServiceCostLoading(false);
		} catch (error) {
			setServiceCostLoading(false);
		}
	};

	const getAnnouncements = async () => {
		try {
			setIsLoading(true);

			const response = await onGetAnnouncementList(getCurrentPage());

			setRequests(response.announcement_actions);
			setTotalRecords(response.pagination.total);

			setIsLoading(false);
		} catch (error) {
			setIsLoading(false);
			NotificationService.error(t('COMMON.ERROR'), t('COMMON.FETCH_ERROR'));
		}
	};

	const fetchMoreData = async (page: number) => {
		try {
			const params = new URLSearchParams(location.search);
			params.set('page', page.toString());
			navigate(`${location.pathname}?${params.toString()}`, { replace: true });

			setIsLoading(true);
			window.scrollTo({ behavior: 'smooth', top: 0 });

			const response = await onGetAnnouncementList(getCurrentPage());
			setRequests(response.announcement_actions);

			setCurrentPage(page);
			setIsLoading(false);
		} catch (error) {
			NotificationService.error(t('COMMON.ERROR'), t('COMMON.FETCH_ERROR'));
			setIsLoading(false);
		}
	};

	return (
		<>
			<ActionListHeaderComponent
				title={t('ACTIONS.ANNOUNCEMENT')}
				subTitle={t('ACTIONS.PUBLISH_ANNOUNCEMENT')}
				costLoading={serviceCostLoading}
				costs={[serviceCost!]}
			/>

			<Table
				columns={columns}
				dataSource={requests}
				scroll={{ x: 600 }}
				rowKey="id"
				loading={isLoading}
				pagination={{
					pageSize: 15,
					total: totalRecords,
					showSizeChanger: false,
					current: currentPage,
					onChange: fetchMoreData,
					showTotal: (total) => <TableTotal total={total} />,
				}}
				locale={{ emptyText: <NoData /> }}
			/>
		</>
	);
};

export default AnnouncementListPage;
